/**
 * LifecyclePage Component
 * AIDLC Administrative Backend
 *
 * Module 04: AIDLC Lifecycle
 * Visual representation of the AI Development Lifecycle state machine.
 * Shows project progress through the 7 AIDLC states:
 *   Intent -> Requirements -> Planning -> Estimating -> Implementing -> Delivering -> Complete
 *
 * Features:
 * - State machine pipeline visualization
 * - Project list with current state
 * - State transition controls (Admin only)
 * - Timeline/history view per project
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { Modal } from '../shared/Modal';
import { Card } from '../shared/Card';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { getAllProjects, transitionProjectState } from '../../services/projectService';
import type { Project, AidlcState } from '../../types/project';

const AIDLC_STATES: { key: AidlcState; label: string; description: string }[] = [
  { key: 'intent', label: 'Intent', description: 'Customer intent captured and validated' },
  { key: 'requirements', label: 'Requirements', description: 'Functional and non-functional requirements defined' },
  { key: 'planning', label: 'Planning', description: 'Architecture, tasks, and milestones planned' },
  { key: 'estimating', label: 'Estimating', description: 'Token budgets, timelines, and resource allocation' },
  { key: 'implementing', label: 'Implementing', description: 'Code generation, testing, and integration' },
  { key: 'delivering', label: 'Delivering', description: 'Deployment, validation, and handoff' },
  { key: 'complete', label: 'Complete', description: 'Project delivered and closed' },
];

const stateColors: Record<AidlcState, string> = {
  intent: 'bg-purple-500',
  requirements: 'bg-blue-500',
  planning: 'bg-cyan-500',
  estimating: 'bg-yellow-500',
  implementing: 'bg-orange-500',
  delivering: 'bg-green-500',
  complete: 'bg-gray-500',
};

const stateBadgeVariant: Record<AidlcState, 'default' | 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  intent: 'info',
  requirements: 'info',
  planning: 'warning',
  estimating: 'warning',
  implementing: 'danger',
  delivering: 'success',
  complete: 'neutral',
};

export function LifecyclePage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [transitionNotes, setTransitionNotes] = useState('');
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const data = await getAllProjects();
        setProjects(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load projects');
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []);

  const getNextState = (current: AidlcState): AidlcState | null => {
    const idx = AIDLC_STATES.findIndex((s) => s.key === current);
    if (idx < 0 || idx >= AIDLC_STATES.length - 1) return null;
    return AIDLC_STATES[idx + 1].key;
  };

  const getStateIndex = (state: AidlcState): number => {
    return AIDLC_STATES.findIndex((s) => s.key === state);
  };

  const handleTransition = async () => {
    if (!selectedProject) return;
    const nextState = getNextState(selectedProject.aidlcState);
    if (!nextState) return;

    setIsTransitioning(true);
    try {
      const updated = await transitionProjectState(selectedProject.id, nextState, transitionNotes);
      setProjects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setSelectedProject(updated);
      setShowTransitionModal(false);
      setTransitionNotes('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to transition state');
    } finally {
      setIsTransitioning(false);
    }
  };

  if (isLoading) return <LoadingSpinner message="Loading AIDLC projects..." />;

  return (
    <div>
      {/* AIDLC Pipeline Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h3 className="text-sm font-semibold text-ldaja-black mb-4">AIDLC State Machine Pipeline</h3>
        <div className="flex items-center gap-0 overflow-x-auto pb-2">
          {AIDLC_STATES.map((state, idx) => {
            const projectsInState = projects.filter((p) => p.aidlcState === state.key);
            return (
              <React.Fragment key={state.key}>
                <div className="flex flex-col items-center min-w-[110px]">
                  <div className={`w-10 h-10 rounded-full ${stateColors[state.key]} flex items-center justify-center text-white text-sm font-bold`}>
                    {idx + 1}
                  </div>
                  <p className="text-xs font-medium text-ldaja-black mt-1.5">{state.label}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{projectsInState.length} project{projectsInState.length !== 1 ? 's' : ''}</p>
                </div>
                {idx < AIDLC_STATES.length - 1 && (
                  <div className="flex-shrink-0 w-8 h-0.5 bg-gray-200 mt-[-20px]" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-xs">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600 text-xs underline mt-1">Dismiss</button>
        </div>
      )}

      {/* Project List */}
      {projects.length === 0 ? (
        <EmptyState
          title="No projects found"
          description="Create a project to begin tracking it through the AIDLC lifecycle."
        />
      ) : (
        <div className="space-y-4">
          {projects.map((project) => {
            const stateIdx = getStateIndex(project.aidlcState);
            const progress = ((stateIdx + 1) / AIDLC_STATES.length) * 100;
            const nextState = getNextState(project.aidlcState);
            const isSelected = selectedProject?.id === project.id;

            return (
              <div
                key={project.id}
                className={`bg-white border rounded-lg overflow-hidden cursor-pointer transition-all ${
                  isSelected ? 'border-ldaja-blue shadow-md' : 'border-gray-200 hover:shadow-sm'
                }`}
                onClick={() => setSelectedProject(isSelected ? null : project)}
              >
                {/* Progress bar */}
                <div className="h-1 bg-gray-100">
                  <div
                    className={`h-full ${stateColors[project.aidlcState]} transition-all`}
                    style={{ width: `${progress}%` }}
                  />
                </div>

                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-semibold text-ldaja-black">{project.name}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{project.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={stateBadgeVariant[project.aidlcState]} dot>
                        {AIDLC_STATES[stateIdx]?.label || project.aidlcState}
                      </Badge>
                      {nextState && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProject(project);
                            setShowTransitionModal(true);
                          }}
                        >
                          Advance
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* State Pipeline Mini-view */}
                  <div className="flex items-center gap-1 mb-3">
                    {AIDLC_STATES.map((s, idx) => (
                      <div
                        key={s.key}
                        className={`h-1.5 flex-1 rounded-full ${
                          idx <= stateIdx ? stateColors[project.aidlcState] : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Project Details */}
                  <div className="flex items-center gap-4 text-[10px] text-gray-500">
                    <span>{project.tasks.length} tasks</span>
                    <span>{project.tasks.filter((t) => t.status === 'completed').length} completed</span>
                    <span>{project.assignedAgents.length} agents</span>
                    <span>{project.milestones.length} milestones</span>
                    <span>{project.risks.filter((r) => r.status === 'open').length} open risks</span>
                  </div>

                  {/* Expanded Details */}
                  {isSelected && (
                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
                      {/* State History Timeline */}
                      {project.stateHistory.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 mb-2">State History</h4>
                          <div className="space-y-2">
                            {project.stateHistory.map((transition, idx) => (
                              <div key={idx} className="flex items-start gap-3">
                                <div className="flex flex-col items-center">
                                  <div className={`w-2.5 h-2.5 rounded-full ${stateColors[transition.toState]}`} />
                                  {idx < project.stateHistory.length - 1 && (
                                    <div className="w-0.5 h-6 bg-gray-200" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-gray-700">
                                      {transition.fromState} &rarr; {transition.toState}
                                    </span>
                                    <span className="text-[10px] text-gray-400">
                                      {new Date(transition.timestamp).toLocaleDateString()}
                                    </span>
                                  </div>
                                  {transition.notes && (
                                    <p className="text-[10px] text-gray-500 mt-0.5">{transition.notes}</p>
                                  )}
                                  <p className="text-[10px] text-gray-400">by {transition.triggeredBy}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tasks */}
                      {project.tasks.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 mb-2">Tasks</h4>
                          <div className="space-y-1">
                            {project.tasks.map((task) => (
                              <div key={task.id} className="flex items-center gap-2 text-xs">
                                <span className={`w-1.5 h-1.5 rounded-full ${
                                  task.status === 'completed' ? 'bg-green-500' :
                                  task.status === 'in-progress' ? 'bg-blue-500' :
                                  task.status === 'blocked' ? 'bg-red-500' : 'bg-gray-400'
                                }`} />
                                <span className="text-gray-700 flex-1">{task.title}</span>
                                <span className="text-gray-400">{task.assignedTo}</span>
                                <Badge variant={
                                  task.status === 'completed' ? 'success' :
                                  task.status === 'in-progress' ? 'info' :
                                  task.status === 'blocked' ? 'danger' : 'neutral'
                                }>
                                  {task.status}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Decisions */}
                      {project.decisions.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 mb-2">Decisions</h4>
                          <div className="space-y-2">
                            {project.decisions.map((d) => (
                              <div key={d.id} className="bg-gray-50 rounded p-2">
                                <p className="text-xs font-medium text-gray-700">{d.title}</p>
                                <p className="text-[10px] text-gray-500 mt-0.5">{d.decision}</p>
                                <p className="text-[10px] text-gray-400 mt-0.5">
                                  {d.decidedBy} -- {new Date(d.decidedAt).toLocaleDateString()}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Risks */}
                      {project.risks.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 mb-2">Risks</h4>
                          <div className="space-y-1">
                            {project.risks.map((risk) => (
                              <div key={risk.id} className="flex items-center gap-2 text-xs">
                                <Badge variant={
                                  risk.level === 'high' ? 'danger' :
                                  risk.level === 'medium' ? 'warning' : 'neutral'
                                }>
                                  {risk.level}
                                </Badge>
                                <span className="text-gray-700 flex-1">{risk.description}</span>
                                <Badge variant={
                                  risk.status === 'open' ? 'warning' :
                                  risk.status === 'mitigated' ? 'success' : 'neutral'
                                }>
                                  {risk.status}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* State Transition Modal */}
      <Modal
        isOpen={showTransitionModal}
        onClose={() => { setShowTransitionModal(false); setTransitionNotes(''); }}
        title="Advance AIDLC State"
        size="sm"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setShowTransitionModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleTransition}
              isLoading={isTransitioning}
            >
              Advance State
            </Button>
          </>
        }
      >
        {selectedProject && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-medium text-ldaja-black">{selectedProject.name}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant={stateBadgeVariant[selectedProject.aidlcState]} dot>
                  {selectedProject.aidlcState}
                </Badge>
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
                {getNextState(selectedProject.aidlcState) && (
                  <Badge variant={stateBadgeVariant[getNextState(selectedProject.aidlcState)!]} dot>
                    {getNextState(selectedProject.aidlcState)}
                  </Badge>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Transition Notes</label>
              <textarea
                value={transitionNotes}
                onChange={(e) => setTransitionNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
                placeholder="Why is this state transition happening? What criteria were met?"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
